import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSpinner,
    faChartLine,
    faTable,
    faExclamationTriangle,
    faFileCsv,
    faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { buildCsv, downloadTextFile } from '../../reports/shared/reportUtils';

const { rootUrl } = require('../../../../config/config');

const normalizeType = (trans) => String(trans?.car_trans_type || trans?.type || 'Unknown').trim() || 'Unknown';

const formatMoney = (value) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));

const getDefaultDateRange = () => {
    const today = new Date();
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(today.getDate() - 90);

    return {
        from: ninetyDaysAgo.toISOString().split('T')[0],
        to: today.toISOString().split('T')[0]
    };
};

const getInitialDateRange = () => {
    if (typeof window !== 'undefined') {
        const saved = localStorage.getItem('exec_summary_date_range');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed?.from && parsed?.to) {
                    return parsed;
                }
            } catch (e) {
                console.error('Error parsing saved date range:', e);
            }
        }
    }

    return getDefaultDateRange();
};

const formatDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

const REMITTANCE_SUBTYPE_LABELS = {
    WKI: 'Weekly Invoice',
    WKR: 'Weekly Remittance',
    LSS: 'Losses',
    ADJ: 'Adjustments',
};

const REMITTANCE_SUBTYPES = new Set(['WKI', 'WKR', 'LSS', 'ADJ']);

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const buildExecutiveSummaryExportRows = (rows = [], expandedSubTypes = []) => {
    const expandedSet = new Set(expandedSubTypes);

    return rows.flatMap((row) => {
        const summaryRow = {
            type: row.subType,
            description: row.label,
            date: '',
            docNo: '',
            debit: row.debit ? formatMoney(row.debit) : '',
            credit: row.credit ? formatMoney(row.credit) : '',
            level: 'Summary'
        };

        if (!expandedSet.has(row.subType)) {
            return [summaryRow];
        }

        const detailRows = row.items.map((item) => ({
            type: `↳ ${item.rawType || row.subType}`,
            description: item.shortDesc || item.label || item.rawType || 'Transaction',
            date: formatDate(item.date),
            docNo: item.docNo,
            debit: item.debit ? formatMoney(item.debit) : '',
            credit: item.credit ? formatMoney(item.credit) : '',
            level: 'Detail'
        }));

        return [summaryRow, ...detailRows];
    });
};

const saveCanvasAsPdf = (canvas, filename) => {
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const marginX = 10;
    const marginY = 10;
    const contentWidth = pageWidth - (marginX * 2);
    const imageWidth = contentWidth;
    const imageHeight = (canvas.height * imageWidth) / canvas.width;
    const pageBodyHeight = pageHeight - (marginY * 2);
    const totalPages = Math.max(1, Math.ceil(imageHeight / pageBodyHeight));
    const imageData = canvas.toDataURL('image/png', 1.0);

    for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
        if (pageIndex > 0) {
            pdf.addPage();
        }

        const imageY = marginY - (pageIndex * pageBodyHeight);
        pdf.addImage(imageData, 'PNG', marginX, imageY, imageWidth, imageHeight);
    }

    pdf.save(filename);
};

const downloadExecutiveSummaryPdf = async ({ rows, plateNo, from, to, expandedSubTypes = [] }) => {
    const exportRows = buildExecutiveSummaryExportRows(rows, expandedSubTypes);
    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = '-10000px';
    wrapper.style.top = '0';
    wrapper.style.background = '#ffffff';
    wrapper.style.padding = '16px';
    wrapper.style.width = '1400px';
    wrapper.style.pointerEvents = 'none';

    const tableHtml = `
        <table style="width:100%; border-collapse:collapse; font-family:Arial, sans-serif; font-size:12px; color:#111827;">
            <thead>
                <tr>
                    <th style="padding:10px 12px; text-align:left; background:#fff7f2; border:1px solid #e5e7eb;">Type</th>
                    <th style="padding:10px 12px; text-align:left; background:#fff7f2; border:1px solid #e5e7eb;">Description</th>
                    <th style="padding:10px 12px; text-align:left; background:#fff7f2; border:1px solid #e5e7eb;">Date</th>
                    <th style="padding:10px 12px; text-align:left; background:#fff7f2; border:1px solid #e5e7eb;">Doc No</th>
                    <th style="padding:10px 12px; text-align:right; background:#fff7f2; border:1px solid #e5e7eb;">Debit</th>
                    <th style="padding:10px 12px; text-align:right; background:#fff7f2; border:1px solid #e5e7eb;">Credit</th>
                </tr>
            </thead>
            <tbody>
                ${exportRows.map((row) => `
                    <tr>
                        <td style="padding:8px 12px; border:1px solid #e5e7eb; font-weight:${row.level === 'Summary' ? 700 : 500}; color:${row.level === 'Summary' ? '#111827' : '#374151'};">${escapeHtml(row.type)}</td>
                        <td style="padding:8px 12px; border:1px solid #e5e7eb; color:#374151;">${escapeHtml(row.description)}</td>
                        <td style="padding:8px 12px; border:1px solid #e5e7eb; color:#6b7280; white-space:nowrap;">${escapeHtml(row.date)}</td>
                        <td style="padding:8px 12px; border:1px solid #e5e7eb; color:#6b7280; white-space:nowrap;">${escapeHtml(row.docNo)}</td>
                        <td style="padding:8px 12px; border:1px solid #e5e7eb; text-align:right; color:#15803d; font-weight:600;">${escapeHtml(row.debit)}</td>
                        <td style="padding:8px 12px; border:1px solid #e5e7eb; text-align:right; color:#b91c1c; font-weight:600;">${escapeHtml(row.credit)}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;

    wrapper.innerHTML = tableHtml;
    document.body.appendChild(wrapper);

    try {
        await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
        const canvas = await html2canvas(wrapper, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            width: wrapper.scrollWidth,
            height: wrapper.scrollHeight,
            windowWidth: wrapper.scrollWidth,
            windowHeight: wrapper.scrollHeight
        });

        const datePart = `${from}_to_${to}`;
        saveCanvasAsPdf(canvas, `executive_summary_${plateNo}_${datePart}.pdf`);
    } finally {
        wrapper.remove();
    }
};

const ExecutiveSummary = ({ plate_no }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState([]);
    const [typeMetaMap, setTypeMetaMap] = useState({});
    const [expandedSubTypes, setExpandedSubTypes] = useState([]);
    const [dateRange, setDateRange] = useState(() => getInitialDateRange());
    const [draftDateRange, setDraftDateRange] = useState(() => getInitialDateRange());
    const [exportingFormat, setExportingFormat] = useState('');

    useEffect(() => {
        localStorage.setItem('exec_summary_date_range', JSON.stringify(dateRange));
    }, [dateRange]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken') || 'default-token';
            
            const [transResponse, typesResponse] = await Promise.all([
                axios.get(`${rootUrl}/cars/${plate_no}/trans`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        start_date: dateRange.from,
                        end_date: dateRange.to,
                        limit: 1000 
                    }
                }),
                axios.get(`${rootUrl}/payments/types/car`, { 
                    headers: { Authorization: `Bearer ${token}` } 
                })
            ]);
            
            const metaMap = {};
            if (Array.isArray(typesResponse.data)) {
                typesResponse.data.forEach(item => {
                    metaMap[item.type] = {
                        subType: item.sub_type || 'OTHER',
                        label: item.long_desc || item.short_desc || item.type
                    };
                });
            }
            setTypeMetaMap(metaMap);
            
            setData(Array.isArray(transResponse.data) ? transResponse.data : []);
            setError(null);
        } catch (err) {
            console.error('Error fetching summary data:', err);
            if (err.response?.status === 404) {
                setData([]);
                setError(null);
            } else {
                setError('Failed to load transaction summary.');
            }
        } finally {
            setLoading(false);
        }
    }, [dateRange.from, dateRange.to, plate_no]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const summary = useMemo(() => {
        const totals = {};
        const weeklyData = {};

        data.forEach(trans => {
            const rawType = normalizeType(trans);
            const meta = typeMetaMap[rawType];
            const subType = meta?.subType || 'OTHER';
            if (!REMITTANCE_SUBTYPES.has(subType)) {
                return;
            }
            const debit = Number(trans.debit || 0);
            const credit = Number(trans.credit || 0);
            const net = Number.isFinite(debit - credit) && (debit !== 0 || credit !== 0)
                ? debit - credit
                : Number(trans.amount || 0);
            const week = trans.week || 'Unknown';
            const item = {
                id: trans.id || `${subType}-${trans.doc_no || 'tx'}-${trans.date || ''}-${trans.input_date || ''}`,
                rawType,
                subType,
                label: meta?.label || rawType,
                shortDesc: trans.short_desc || meta?.label || rawType,
                docNo: trans.doc_no || 'N/A',
                date: trans.date || trans.input_date || null,
                debit,
                credit,
                net,
                amount: net
            };

            if (!totals[subType]) {
                totals[subType] = { subType, debit: 0, credit: 0, net: 0, count: 0, items: [] };
            }
            totals[subType].debit += debit;
            totals[subType].credit += credit;
            totals[subType].net += net;
            totals[subType].count += 1;
            totals[subType].items.push(item);

            if (!weeklyData[week]) {
                weeklyData[week] = { week };
            }
            weeklyData[week][subType] = (weeklyData[week][subType] || 0) + Math.abs(net);
        });

        const rows = Object.values(totals)
            .map((item) => ({
                subType: item.subType,
                label: REMITTANCE_SUBTYPE_LABELS[item.subType] || item.subType,
                debit: item.net > 0 ? item.net : 0,
                credit: item.net < 0 ? Math.abs(item.net) : 0,
                net: item.net,
                count: item.count,
                items: item.items,
            }))
            .filter((item) => item.net !== 0)
            .sort((a, b) => a.subType.localeCompare(b.subType));

        const chartData = Object.values(weeklyData).sort((a, b) => a.week.localeCompare(b.week));
        const sortedTypes = Array.from(new Set(rows.map((row) => row.subType))).sort();

        return {
            rows,
            chartData,
            types: sortedTypes,
            totals: rows.reduce((acc, row) => {
                acc.debit += row.debit;
                acc.credit += row.credit;
                acc.net += row.net;
                return acc;
            }, { debit: 0, credit: 0, net: 0 })
        };
    }, [data, typeMetaMap]);

    const isDraftRangeValid = useMemo(
        () => draftDateRange.from && draftDateRange.to && draftDateRange.from <= draftDateRange.to,
        [draftDateRange.from, draftDateRange.to]
    );

    const exportRows = useMemo(
        () => buildExecutiveSummaryExportRows(summary.rows, expandedSubTypes),
        [summary.rows, expandedSubTypes]
    );

    const applyDateRange = useCallback(() => {
        if (!isDraftRangeValid) {
            return;
        }

        if (draftDateRange.from === dateRange.from && draftDateRange.to === dateRange.to) {
            return;
        }

        setDateRange({ ...draftDateRange });
    }, [dateRange.from, dateRange.to, draftDateRange, isDraftRangeValid]);

    const resetDateRange = useCallback(() => {
        const defaults = getDefaultDateRange();
        setDraftDateRange(defaults);
        setDateRange(defaults);
    }, []);

    const handleExportCsv = useCallback(() => {
        if (exportRows.length === 0) {
            return;
        }

        const filename = `executive_summary_${plate_no}_${dateRange.from}_to_${dateRange.to}.csv`;
        const csv = buildCsv(
            ['Type', 'Description', 'Date', 'Doc No', 'Debit', 'Credit'],
            exportRows.map((row) => [row.type, row.description, row.date, row.docNo, row.debit, row.credit])
        );
        downloadTextFile(`\uFEFF${csv}`, filename, 'text/csv;charset=utf-8');
    }, [dateRange.from, dateRange.to, exportRows, plate_no]);

    const handleExportPdf = useCallback(async () => {
        if (exportRows.length === 0 || exportingFormat) {
            return;
        }

        try {
            setExportingFormat('pdf');
            await downloadExecutiveSummaryPdf({
                rows: summary.rows,
                plateNo: plate_no,
                from: dateRange.from,
                to: dateRange.to,
                expandedSubTypes
            });
        } catch (err) {
            console.error('Failed to export executive summary PDF:', err);
            setError('Failed to export the summary PDF.');
        } finally {
            setExportingFormat('');
        }
    }, [dateRange.from, dateRange.to, expandedSubTypes, exportingFormat, exportRows.length, plate_no, summary.rows]);

    const handleExportCsvClick = useCallback(() => {
        if (exportRows.length === 0 || exportingFormat) {
            return;
        }

        try {
            setExportingFormat('csv');
            handleExportCsv();
        } catch (err) {
            console.error('Failed to export executive summary CSV:', err);
            setError('Failed to export the summary CSV.');
        } finally {
            setExportingFormat('');
        }
    }, [exportRows.length, exportingFormat, handleExportCsv]);

    const toggleSubType = (subType) => {
        setExpandedSubTypes((prev) => (
            prev.includes(subType)
                ? prev.filter((item) => item !== subType)
                : [...prev, subType]
        ));
    };

    const isExpanded = (subType) => expandedSubTypes.includes(subType);

    const renderTransactionDetails = (items) => (
        <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Specific transactions</div>
                <div className="text-xs text-gray-500">{items.length} rows</div>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                    <thead className="bg-white text-[10px] uppercase text-gray-500">
                        <tr>
                            <th className="px-4 py-3 text-left">Date</th>
                            <th className="px-4 py-3 text-left">Doc No</th>
                            <th className="px-4 py-3 text-left">Description</th>
                            <th className="px-4 py-3 text-right">Debit</th>
                            <th className="px-4 py-3 text-right">Credit</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {items.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="px-4 py-4 text-center text-gray-500">
                                    No transactions found for this subtype.
                                </td>
                            </tr>
                        ) : items.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(item.date)}</td>
                                <td className="px-4 py-3 font-mono text-gray-800 whitespace-nowrap">{item.docNo}</td>
                                <td className="px-4 py-3 text-gray-700">
                                    <div className="font-medium">{item.shortDesc}</div>
                                    <div className="text-[11px] text-gray-500">{item.rawType}</div>
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-green-700 whitespace-nowrap">
                                    {item.debit ? formatMoney(item.debit) : '—'}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-red-700 whitespace-nowrap">
                                    {item.credit ? formatMoney(item.credit) : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const colors = ['#FF5733', '#33FF57', '#3357FF', '#F333FF', '#FF33A8', '#33FFF3', '#F3FF33', '#FF8333'];

    if (loading) return (
        <div className="flex justify-center items-center py-20">
            <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-[#FF5733]" />
            <span className="ml-4 text-gray-600 font-medium">Analyzing transaction data for {plate_no}...</span>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Header & Filter Bar */}
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-none">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div className="flex items-center gap-3">
                        <div className="rounded-sm bg-[#FF5733] bg-opacity-10 p-2">
                            <FontAwesomeIcon icon={faChartLine} className="text-sm text-[#FF5733]" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-gray-800">Executive Summary</h2>
                            <p className="text-xs font-medium text-gray-500">{data.length} Transactions Analyzed</p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={handleExportCsvClick}
                            disabled={!exportRows.length || loading || Boolean(exportingFormat)}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:border-orange-300 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {exportingFormat === 'csv' ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faFileCsv} />}
                            CSV
                        </button>
                        <button
                            type="button"
                            onClick={handleExportPdf}
                            disabled={!exportRows.length || loading || Boolean(exportingFormat)}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:border-orange-300 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {exportingFormat === 'pdf' ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faFilePdf} />}
                            PDF
                        </button>
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap items-end gap-3">
                    <label className="flex w-full max-w-[155px] flex-col gap-1 sm:w-[155px]">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">From</span>
                        <input
                            type="date"
                            value={draftDateRange.from}
                            onChange={(e) => setDraftDateRange((prev) => ({ ...prev, from: e.target.value }))}
                            className="h-8 w-full rounded-md border border-gray-200 bg-white px-2.5 text-sm font-semibold text-gray-700 outline-none shadow-sm focus:border-orange-300"
                        />
                    </label>
                    <label className="flex w-full max-w-[155px] flex-col gap-1 sm:w-[155px]">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">To</span>
                        <input
                            type="date"
                            value={draftDateRange.to}
                            onChange={(e) => setDraftDateRange((prev) => ({ ...prev, to: e.target.value }))}
                            className="h-8 w-full rounded-md border border-gray-200 bg-white px-2.5 text-sm font-semibold text-gray-700 outline-none shadow-sm focus:border-orange-300"
                        />
                    </label>
                    <button
                        type="button"
                        onClick={applyDateRange}
                        disabled={!isDraftRangeValid || loading}
                        className="inline-flex h-8 items-center justify-center rounded-md bg-[#FF5733] px-3.5 text-sm font-semibold text-white transition hover:bg-[#e24d2d] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Apply Range
                    </button>
                    <button
                        type="button"
                        onClick={resetDateRange}
                        disabled={loading}
                        className="inline-flex h-8 items-center justify-center rounded-md border border-gray-200 bg-white px-3.5 text-sm font-semibold text-gray-700 transition hover:border-orange-300 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Reset
                    </button>
                </div>

                {!isDraftRangeValid && (
                    <p className="mt-2 text-xs font-medium text-amber-600">
                        Start date must be earlier than or equal to the end date.
                    </p>
                )}

                <p className="mt-2 text-xs font-medium text-gray-500">
                    Results refresh only after you click Apply Range.
                </p>
            </div>

            {error && (
                <div className="bg-red-50 text-red-600 p-4 rounded-sm border border-red-100 flex items-center gap-3">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span className="text-sm font-medium">{error}</span>
                </div>
            )}

            {data.length === 0 && !loading && !error ? (
                <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                    <FontAwesomeIcon icon={faTable} className="text-4xl text-gray-300 mb-4" />
                    <p className="text-gray-500 font-medium">No transactions found for the selected period.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Mobile totals cards */}
                    <div className="md:hidden space-y-3">
                        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-none">
                            <div className="flex items-center gap-2 mb-3">
                                <FontAwesomeIcon icon={faTable} className="text-[#FF5733]" />
                                <h3 className="font-bold text-gray-800">Totals per subtype</h3>
                            </div>
                            {summary.rows.length === 0 ? (
                                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                                    No remittance subtypes have a non-zero net for the selected period.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {summary.rows.map((row) => (
                                        <div key={row.subType} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                                            <div className="flex items-start justify-between gap-3">
                                                <label className="flex items-start gap-3 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={isExpanded(row.subType)}
                                                        onChange={() => toggleSubType(row.subType)}
                                                        className="mt-1 h-4 w-4 rounded border-gray-300 text-[#FF5733] focus:ring-[#FF5733]"
                                                        aria-label={`Toggle ${row.subType} details`}
                                                    />
                                                    <div className="text-sm font-bold text-gray-700">
                                                        <span className="bg-gray-100 px-2 py-1 rounded text-xs">{row.subType}</span>
                                                        <div className="mt-1 text-xs font-medium text-gray-500">{row.label}</div>
                                                    </div>
                                                </label>
                                                <div className="text-right">
                                                    <div className="text-xs text-gray-500">Lines</div>
                                                    <div className="font-bold text-gray-900">{row.count}</div>
                                                </div>
                                            </div>
                                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                                                <div className="rounded-md bg-white px-2 py-2 border border-gray-100">
                                                    <div className="text-gray-500">Debit</div>
                                                    <div className="font-semibold text-green-700">{row.debit ? formatMoney(row.debit) : '—'}</div>
                                                </div>
                                                <div className="rounded-md bg-white px-2 py-2 border border-gray-100">
                                                    <div className="text-gray-500">Credit</div>
                                                    <div className="font-semibold text-red-700">{row.credit ? formatMoney(row.credit) : '—'}</div>
                                                </div>
                                            </div>
                                            {isExpanded(row.subType) && renderTransactionDetails(row.items)}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
                                <span className="text-sm font-bold text-gray-800">Debit / Credit Totals</span>
                                <span className="text-sm font-black text-gray-700">
                                    {formatMoney(summary.totals.debit)} / {formatMoney(summary.totals.credit)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Totals Table - Top Position, Responsive Grid */}
                    <div className="hidden md:block bg-white rounded-xl border border-gray-100 overflow-hidden shadow-none">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faTable} className="text-[#FF5733]" />
                                <h3 className="font-bold text-gray-800">Totals per subtype</h3>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-gray-25 text-[10px] uppercase text-gray-500 font-bold">
                                    <tr>
                                        <th className="px-4 py-4 border-b border-gray-100 w-10"></th>
                                        <th className="px-6 py-4 border-b border-gray-100">Subtype</th>
                                        <th className="px-6 py-4 border-b border-gray-100 text-right">Debit (Ksh)</th>
                                        <th className="px-6 py-4 border-b border-gray-100 text-right">Credit (Ksh)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {summary.rows.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-8 text-center text-sm text-gray-500">
                                                No remittance subtypes have a non-zero net for the selected period.
                                            </td>
                                        </tr>
                                    ) : summary.rows.map((row) => (
                                        <React.Fragment key={row.subType}>
                                            <tr className="hover:bg-gray-25 transition-colors group">
                                                <td className="px-4 py-4 align-top">
                                                    <input
                                                        type="checkbox"
                                                        checked={isExpanded(row.subType)}
                                                        onChange={() => toggleSubType(row.subType)}
                                                        className="h-4 w-4 rounded border-gray-300 text-[#FF5733] focus:ring-[#FF5733]"
                                                        aria-label={`Toggle ${row.subType} details`}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 text-sm font-bold text-gray-700">
                                                    <span className="bg-gray-100 px-2 py-1 rounded text-xs">{row.subType}</span>
                                                    <div className="mt-1 text-xs font-medium text-gray-500">{row.label}</div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-right font-bold text-green-700">
                                                    {row.debit ? formatMoney(row.debit) : '—'}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-right font-bold text-red-700">
                                                    {row.credit ? formatMoney(row.credit) : '—'}
                                                </td>
                                            </tr>
                                            {isExpanded(row.subType) && (
                                                <tr className="bg-gray-50">
                                                    <td colSpan="4" className="px-6 py-4">
                                                        {renderTransactionDetails(row.items)}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50 border-t-2 border-gray-100">
                                    <tr>
                                        <td className="px-4 py-5 text-sm font-bold text-gray-800" colSpan="2">Totals:</td>
                                        <td className="px-6 py-5 text-sm text-right font-black text-green-700">
                                            {formatMoney(summary.totals.debit)}
                                        </td>
                                        <td className="px-6 py-5 text-sm text-right font-black text-red-700">
                                            {formatMoney(summary.totals.credit)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    {/* Chart - Bottom Position */}
                    <div className="bg-white rounded-xl border border-gray-100 p-4 sm:p-6 shadow-none">
                        <div className="flex items-center gap-2 mb-8">
                            <div className="p-2 bg-[#FF5733] bg-opacity-10 rounded-lg">
                                <FontAwesomeIcon icon={faChartLine} className="text-[#FF5733]" />
                            </div>
                            <h3 className="font-bold text-gray-800">Weekly Performance Trends</h3>
                        </div>
                                    <div className="h-[300px] md:h-[450px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={summary.chartData} margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis 
                                        dataKey="week" 
                                        axisLine={false} 
                                        tickLine={false} 
                                        tick={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
                                        dy={15}
                                    />
                                    <YAxis 
                                        axisLine={false} 
                                        tickLine={false} 
                                        tick={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
                                        tickFormatter={(val) => `Ksh ${val >= 1000 ? (val/1000).toFixed(0) + 'k' : val}`}
                                    />
                                    <Tooltip 
                                        contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)', padding: '16px' }}
                                        itemStyle={{ fontSize: '13px', padding: '4px 0' }}
                                        labelStyle={{ fontWeight: 'bold', marginBottom: '10px', color: '#111827', fontSize: '14px' }}
                                        formatter={(value, name) => [
                                            <span className="font-bold text-gray-900">{formatMoney(value)}</span>, 
                                            <span className="text-gray-600">{REMITTANCE_SUBTYPE_LABELS[name] || name}</span>
                                        ]}
                                    />
                                    <Legend 
                                        iconType="circle" 
                                        verticalAlign="bottom"
                                        height={36}
                                        wrapperStyle={{ paddingTop: '40px' }}
                                        formatter={(value) => <span className="text-xs font-semibold text-gray-600 px-2">{REMITTANCE_SUBTYPE_LABELS[value] || value}</span>}
                                    />
                                    {summary.types.map((type, idx) => (
                                        <Bar 
                                            key={type}
                                            dataKey={type} 
                                            name={type}
                                            fill={colors[idx % colors.length]}
                                            radius={0}
                                            barSize={20}
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExecutiveSummary;
